/** @file The inflate's input as segments: byte arrays in order, or a PNG's IDAT payloads walked lazily. */

/** Where the inflate reads: bytes [at, end) of `cur`. */
export interface Cursor {
  cur: Uint8Array;
  at: number;
  end: number;
}

/** Moves `c` to the next segment (possibly empty); false when there is none. */
export type NextSegment = (c: Cursor) => boolean;

const IDAT = 0x49444154;
const IEND = 0x49454e44;

/** Each array of `parts` in turn. */
function arraySegments(parts: readonly Uint8Array[]): NextSegment {
  let index = 0;
  return (c) => {
    const part = parts[index++];
    if (part === undefined) return false;
    c.cur = part;
    c.at = 0;
    c.end = part.length;
    return true;
  };
}

/**
 * The payload of each IDAT chunk of `png`, from the chunk at `first` to IEND, skipping other chunks: two integers of
 * state, nothing allocated per chunk. The chunk layout was validated by readPngChunks; a length running past the end
 * stops the walk.
 */
function idatSegments(png: Uint8Array, first: number): NextSegment {
  const view = new DataView(png.buffer, png.byteOffset, png.byteLength);
  let at = first < 0 ? png.length : first;
  return (c) => {
    while (at + 12 <= png.length) {
      const length = view.getUint32(at);
      const type = view.getUint32(at + 4);
      const start = at + 8;
      if (type === IEND || length > png.length - start - 4) return false;
      at = start + length + 4;
      if (type !== IDAT) continue;
      c.cur = png;
      c.at = start;
      c.end = start + length;
      return true;
    }
    return false;
  };
}

/** The segments of `input`: a list of byte arrays, or `{ png, first }` (the offset of a PNG's first IDAT chunk). */
export function inputSegments(input: readonly Uint8Array[] | { png: Uint8Array; first: number }): NextSegment {
  return 'png' in input ? idatSegments(input.png, input.first) : arraySegments(input);
}
